import {Vector2} from 'three';

import {checkExhaustive} from 'external/dev_april_corgi+/js/common/asserts';
import * as corgi from 'external/dev_april_corgi+/js/corgi';
import {
  ACTION,
  CHANGED,
  PRESSED,
} from 'external/dev_april_corgi+/js/emu/events';

import {
  Args,
  Divider,
  Menu,
  MenuController,
  MenuItem,
  State,
} from './menu_controller';
import {Input} from './widgets';

export function MenuElement(
  {
    accent,
    anchor,
    bound,
    context,
    items,
    position,
  }: {
    accent: number[];
    anchor: 'first_item' | 'top_left';
    bound: DOMRect;
    context: Args;
    items: Array<Divider | Menu | MenuItem>;
    position: Vector2;
  },
  state: State | undefined,
  updateState: (newState: State) => void,
) {
  if (!state) {
    const size = estimateSize(items);
    const xFraction = size[0] / bound.width;
    const yFraction = size[1] / bound.height;
    state = {
      active: [0],
      items,
      mode: xFraction < 0.75 && yFraction < 0.75 ? 'normal' : 'searching',
      search: '',
      searchIndex: -1,
      searchResults: [],
    };
  }

  let anchored: [number, number];
  if (anchor === 'first_item') {
    // TODO(april): I have no idea where these numbers are from
    anchored = [position.x - 48, position.y - 16];
  } else if (anchor === 'top_left') {
    // +4 to push it down just a little more
    anchored = [position.x, position.y + 4];
  } else {
    throw checkExhaustive(anchor);
  }

  return (
    <div
      className="absolute inset-0"
      js={corgi.bind({
        args: context,
        controller: MenuController,
        events: {
          contextmenu: 'maybeClickClose',
          click: 'maybeClickClose',
          render: 'wakeup',
        },
        // TODO(april): why is this needed?
        key: `${anchored[0]},${anchored[1]}`,
        state: [state, updateState],
      })}
    >
      {state.mode === 'normal' ? (
        <NormalMenu
          accent={accent}
          anchor={anchored}
          bound={bound}
          state={state}
        />
      ) : (
        <></>
      )}
      {state.mode === 'searching' ? (
        <SearchMenu anchor={anchored} state={state} />
      ) : (
        <></>
      )}
    </div>
  );
}

function NormalMenu({
  accent,
  anchor,
  bound,
  state,
}: {
  accent: number[];
  anchor: [x: number, y: number];
  bound: DOMRect;
  state: State;
}) {
  const active = state.active;
  const items = state.items;
  const size = estimateSize(items);
  // TODO(april): we need scroll probably... ugh.
  const newAnchor = [
    Math.max(0, Math.min(anchor[0], bound.width - size[0])),
    Math.max(0, Math.min(anchor[1], bound.height - size[1])),
  ];
  return (
    <div
      className="absolute"
      style={`left: ${newAnchor[0]}px; top: ${newAnchor[1]}px`}
    >
      <SubMenu accent={accent} active={active} items={items} />
    </div>
  );
}

function SubMenu({
  accent,
  active,
  items,
}: {
  accent: number[];
  active: number[];
  items: Array<Divider | Menu | MenuItem>;
}) {
  return (
    <div className="absolute bg-neutral-800 p-1 rounded space-y-1 start-full text-sm -top-1 z-50">
      {items.map((item, i) => (
        <MenuEntry accent={accent} active={active} index={i} item={item} />
      ))}
    </div>
  );
}

function MenuEntry({
  accent,
  active,
  index,
  item,
}: {
  accent: number[];
  active: number[];
  index: number;
  item: Divider | Menu | MenuItem;
}) {
  const isAccent = accent[0] === index;
  const isActive = active[0] === index;
  const lastActive = active.length === 1;

  let element;
  if (item.kind === 'menu_item') {
    element = (
      <div className="cursor-default flex justify-between">
        <div>{item.label}</div>
        <div className="grow w-8"></div>
      </div>
    );
  } else if (item.kind === 'menu') {
    element = (
      <>
        <div className="cursor-default flex justify-between">
          <div>{item.label}</div>
          <div className="grow w-8"></div>
          <div className="ps-1">›</div>
        </div>
        {isActive ? (
          <SubMenu
            accent={isAccent ? accent.slice(1) : []}
            active={active.slice(1)}
            items={item.items}
          />
        ) : (
          <></>
        )}
      </>
    );
  } else if (item.kind === 'divider') {
    element = <div className="bg-neutral-50 h-px -mx-1 my-2" />;
  } else {
    throw checkExhaustive(item);
  }

  return (
    <div
      autofocus={isActive && lastActive}
      className={
        'outline-none px-1 text-nowrap relative rounded z-50' +
        (isAccent ? ' bg-accent-700' : isActive ? ' bg-neutral-600' : '')
      }
      data={{index}}
      tabindex="0"
      unboundEvents={{
        click: 'selected',
        focus: 'focused',
        keyup: 'keyPressed',
        pointerenter: 'pointerEntered',
      }}
    >
      {element}
    </div>
  );
}

function estimateSize(
  items: Array<Divider | Menu | MenuItem>,
): [width: number, height: number] {
  const size: [number, number] = [0, items.length * 24 + 4];
  let offsetY = 4;
  for (const item of items) {
    if (item.kind === 'divider') {
      size[1] += 4;
      continue;
    }

    // Just dumb estimates
    size[0] = Math.max(size[0], 8 * item.label.length + 32 + 8);
    if (item.kind === 'menu') {
      const s = estimateSize(item.items);
      size[0] += s[0];
      size[1] = Math.max(size[1], offsetY + s[1]);
    }
    offsetY += 20 + 4;
  }
  return [size[0], size[1]];
}

function SearchMenu({
  anchor,
  state,
}: {
  anchor: [x: number, y: number];
  state: State;
}) {
  return (
    <div
      className={
        'absolute bg-neutral-800 max-h-72 overflow-y-auto p-1 rounded space-y-1 start-full ' +
        'text-sm -top-1 z-50'
      }
      style={`left: ${anchor[0]}px; top: ${anchor[1]}px`}
    >
      <Input
        autofocus
        unboundEvents={{
          corgi: [
            [ACTION, 'searchAction'],
            [CHANGED, 'searchChanged'],
            [PRESSED, 'searchPressed'],
          ],
        }}
        value={state.search}
      />
      {state.searchResults.map((r, i) => (
        <div
          className={
            'outline-none px-1 select-none text-nowrap relative rounded z-50' +
            (state.searchIndex === i ? ' bg-neutral-600' : '')
          }
          data-i={i}
          unboundEvents={{
            click: 'searchAction',
            pointerenter: 'searchPointerEntered',
          }}
        >
          <span className="text-neutral-400">
            {r.context.join(' ▸ ')}
            {r.context.length > 0 ? ' ▸ ' : ''}
          </span>
          {r.item.label}
        </div>
      ))}
    </div>
  );
}
